import _ from 'lodash'
import Backbone from 'backbone'
import constants from 'scoreboard/components/constants'
import ChooseReport from './choose-report/choose-report-path'

function modelAttrs(path) {
	return path.model ? (path.model.attributes || path.model) : null
}

function visibleSteps(path) {
	//if no visible function, treat as always visible
	return _.isUndefined(path.step.visible) || path.step.visible(path.model)
}

/**
 * Represents the state of the wizard on the client.	In addition it can hold
 * a reference to the Report model that holds the state of the report itself.
 * The wizard state is solely client side
 */
var ReportWizardInfo = Backbone.Model.extend({

	//default state starts at index 0
	defaults: function() {
		return {
			index: 0,
			section: 'reports'
		}
	},

	initialize: function(opts) {
		this._path = []
		this._visited = 0	//track the high step visited so far
		if (opts && opts.path) {
			this.path(opts.path, opts.model)
		} else {
			//default path is the choose report path
			this._path.push({
				step: ChooseReport().steps[0],
				model: this
			})
		}
	},

	transition: function(dist) {
		if (dist == 0) {
			return
		}
		var d = dist || 1	//default moving forward 1
		if (d > 0) {
			let err = this.testNext()
			if (err) {
				this.trigger('toggleErrors', { message: err })
				return
			}
		}

		var newIndex = this.position() + d
		if (newIndex < 0) {
			return  	//guard going out of bounds
		}
		if (newIndex >= this._path.length) {
			this.trigger('done', this.endModel || this.currentModel())	//we're done!
			return
		}

		//check to see if step is optional
		var step = this._path[newIndex].step
		var model = this._path[newIndex].model

		if (step.visible && !step.visible(model)) {
			let direction = dist < 0 ? -1 : 1
			this.transition(d+direction)	//skip optional step
			return
		}

		this._visited = Math.max(this._visited, newIndex)

		//add in hook relayout the wizard if needed
		if (step.layout) {
			//when triggering new layout, set the index silently so that the new layout
			//will access the correct step position in the wizard state
			this.set('index', newIndex, {silent: true})
			this.trigger('layout', step.layout)
		}

		//let anyone listening know before actually change the index
		this.trigger('transition', d)

		//set the new position we're on
		this.set('index', newIndex)
	},

	/**
	 * Returns an object describing the current step of the wizard.	This object
	 * contains data about the step itself
	 */
	currentStep: function() {
		return this._path[this.position()].step
	},

	// returns the model associated with the current step
	currentModel: function() {
		return this._path[this.position()].model
	},

	/**
	 * Returns the current index (i.e. step number) the wizard is currently on
	 */
	position: function() {
		return this.get('index')
	},

	validate: function(attr, opts = {}) {
		let step = this.currentStep()
		let model = this.currentModel()
		//use custom step validation, falling back to model validation if
		//the step does not define one
		let validate = step.validate || model.validate
		if (_.isFunction(validate)) {
			//clear any errors that are showing before validating
			//validation will take care of disabling buttons if necessary
			this.trigger('toggleErrors')

			//validate the step with it's associated model's attributes
			return validate.call(model, model.attributes || model, opts)
		}
	},

	/**
	 * Tests whether the the condition of the current step allows transition,
	 * returns an error message if this is not the case
	 */
	testNext() {
		let step = this.currentStep()
		let model = this.currentModel()
		return step.testNext ? step.testNext.call(model, model.attributes || model) : null
	},

	/**
	 * Updates the path, with option to specify an object as the target model
	 */
	path: function(path, model, opts = {}) {
		var index = this.position()
		if (!path) {
			path = {steps:[]}	//use empty path
		} else if (_.isNil(path.steps)) {
			path.steps = []
		}
		if (path && path.steps.length <= index && opts.replace) {
			return //if we're beyond, do nothing
		}

		//set target model if given, otherwise instantiate new path model if
		//defined
		var pathModel = path.model
		var tar = model || (pathModel ? new pathModel() : null)
		if (tar) {
			tar.section = constants.SECTION_REPORTS
		}

		//no steps or model, do nothing
		if (_.isEmpty(path.steps) && _.isNil(tar)) {
			return
		}

		//splice off the rest of the current steps, adding the new steps
		var newSteps = []
		let startIndex = opts.replace ? index : 0
		for (var i=startIndex; i<path.steps.length; i++) {
			newSteps.push({
				step: path.steps[i],
				model: tar
			})
		}
		this._path = _.take(this._path, index+1).concat(newSteps)

		//emit event notifying the path changed
		this.trigger('path:changed', newSteps)
		if (tar) {
			//if no new steps but new model, use that as the ending model
			if (path.steps.length == 0) {
				this.endModel = tar
			} else {
				delete this.endModel
			}
			//bubble any changes on target model
			this.listenTo(tar, 'change', this.trigger.bind(this, 'path-model:changed'))
		}
	},

	/**
	 * Returns an object with the number of steps visible, completed and remaining
	 * A completed step is a step that meets 2 criterias
	 * 1. it must of have already been visited (or is currently on)
	 * 2. if the step requires validation it must pass
	 */
	progress: function() {
		var completed = 0
		var firstInvalid = function(path, index) {
			var notVisited = this._visited < index
			var notValidated = path.step.validate
				&& path.step.validate(modelAttrs(path))
			var invalid = notVisited || notValidated
			if (!invalid) {
				completed++
			}
			return invalid
		}

		var visible =_.filter(this._path, visibleSteps)
		visible.some(firstInvalid, this)

		return {
			visible: visible.length,
			completed: completed,
			remaining: Math.max(visible.length-(this.position()+1), 0)
		}
	}

})

export default ReportWizardInfo


ReportWizardInfo.__filename__ = 'reports/wizard/reports-wizard-info.js'
